import { useApi } from '@/api';
import { ChartSkeleton, ChartTimeFilter } from '@/components';
import { tokensMapping } from '@/utils/tokens';
import { useTheme } from 'styled-components';
import {
  CurrencyValue,
  Flex,
  BarChart,
  formatDatetime,
  formatNumber,
  DisplaySwitch
} from '@blockanalitica/ui';
import { useState, useCallback, useMemo } from 'react';
import { useChartPeriod } from '@/hooks';

export default function HistoricTabs() {
  const theme = useTheme();
  const [chartDaysAgo, setChartDaysAgo] = useState('30');
  const [chartPeriod, updateChartConfigWithPeriod] =
    useChartPeriod(chartDaysAgo);
  const config = updateChartConfigWithPeriod();

  const timeFilter = useMemo(
    () => (
      <ChartTimeFilter activeOption={chartDaysAgo} onChange={setChartDaysAgo} />
    ),
    [chartDaysAgo]
  );
  const [activeDisplayOption, setActiveDisplayOption] = useState('usds_volume');

  const displayOptions = useMemo(
    () => [
      { key: 'usds_volume', value: 'USDS Volume' },
      { key: 'dai_volume', value: 'DAI Volume' }
    ],
    []
  );
  const [data, error] = useApi({
    url: '/migrations/dai/historic-volume/',
    queryParams: {
      days_ago: chartDaysAgo
    }
  });

  if (error) {
    throw error;
  }

  const valueFormatter = useMemo(() => {
    function formatValue(value: string) {
      switch (activeDisplayOption) {
        case 'dai_volume':
          return (
            <CurrencyValue
              value={value}
              options={{
                currency: tokensMapping('DAI'),
                iconSize: '0'
              }}
            />
          );
        default:
          return (
            <CurrencyValue
              value={value}
              options={{
                currency: tokensMapping('USDS'),
                iconSize: '0'
              }}
            />
          );
      }
    }
    return formatValue;
  }, [activeDisplayOption]);

  const yScaleValueFormatter = (value) => {
    return formatNumber(Number(value));
  };

  const datasets = useMemo(() => {
    switch (activeDisplayOption) {
      case 'dai_volume':
        return [
          {
            xField: 'date',
            yField: 'dai_usds_to_dai_volume',
            label: 'USDS to DAI',
            backgroundColor: theme.colors.chartUp
          },
          {
            xField: 'date',
            yField: 'dai_dai_to_usds_volume',
            label: 'DAI to USDS',
            backgroundColor: theme.colors.chartDown
          }
        ];
      case 'usds_volume':
        return [
          {
            xField: 'date',
            yField: 'usds_dai_to_usds_volume',
            label: 'DAI to USDS',
            backgroundColor: theme.colors.chartUp
          },
          {
            xField: 'date',
            yField: 'usds_usds_to_dai_volume',
            label: 'USDS to DAI',
            backgroundColor: theme.colors.chartDown
          }
        ];
    }
  }, [activeDisplayOption, theme.colors]);

  const subValueFormatter = useCallback(
    (value: string) => (
      <>
        {chartPeriod.labelPrefix}
        {formatDatetime(value, { format: chartPeriod.dateFormat })}
      </>
    ),
    [chartPeriod]
  );

  if (data) {
    return (
      <Flex flexDirection="column" gap="xs">
        <DisplaySwitch
          data={displayOptions}
          activeOption={activeDisplayOption}
          onChange={setActiveDisplayOption}
        />
        <BarChart
          key={activeDisplayOption}
          data={data}
          datasets={datasets}
          valueFormatter={valueFormatter}
          yScaleValueFormatter={yScaleValueFormatter}
          timeFilter={timeFilter}
          config={config}
          subValueFormatter={subValueFormatter}
        />
      </Flex>
    );
  }

  return <ChartSkeleton />;
}
